// Arc Flash Labels page. Admin covering the site.
// Words: AF_LABEL_IMPORT_WORDS.md (session I8). Mockup: MOCKUP_AF_LABELS.html, approved by Robert 2026-10-21.
// Addresses:
//   #/af-labels                    the first site the badge covers
//   #/af-labels/{site}             that site's studies, newest first
//   #/af-labels/{site}/import      Import Study
//   #/af-labels/{site}/s/{studyId} Review, Publish and Publish Check
// Nothing here writes to the database. Import, changes and Publish go through server code (calls 86 to 89).

import "./af-labels.css";
import type { SignInClaims } from "./badge";
import { masterName } from "./drive-folders";
import { assembleMaster } from "./master-pdf";
import { loadMasterState, retryMaster, sendMaster, stopMasterWatches } from "./master-send";
import { afStudyFileUrl, callServer, loadAfLabelPrints, loadAfRegistry, loadAfRows, loadAfSites, loadAfStudies, uploadAfLabelFile, uploadAfStudyFile, type AfStudySummary } from "./firebase";
import { afPrintState, candidateOf, printedEntries, type AfCandidate, type AfPrintRecord, type AfStock } from "./af-label-print";
import { cardClick, cardFind, downloadFile, newPcState, pickedOf, printCard, rollStatus, sectionsOf, seedPicks, shareFile, shortDate, type PcRoll, type PcState } from "./print-card";
import { esc } from "./training-screens";
import { readOffset } from "./printer-offset";
import { isReady, LABEL_FILES, labelRecordsOf, labelSetIdOf, sortStudyFiles, StudyRegistry, type FileSort, type ReadFile, type RegistryAsset, type RowChange, type StoredRow, type StudyInfo } from "./af-study";
import { importScreen, reviewScreen, sheetHtml, studyList, type AfFilter, type AfSheet, type AfSite, type ImportForm, type Measure, roughMeasure } from "./af-labels-screens";
import { errorScreen, loadingScreen } from "./training-screens";

export interface AfHost {
  root: HTMLElement;
  badge: SignInClaims;
  /** Draws the shared header, then `inner` in the page column. */
  frame: (inner: string) => void;
}

let host: AfHost | null = null;
let wired = false;
let sites: AfSite[] | null = null;
let sitesError = "";
const studies = new Map<string, AfStudySummary[] | string>();
const forms = new Map<string, ImportForm & { raw: File[]; read: ReadFile[] }>();

interface Review {
  key: string;
  study: AfStudySummary;
  rows: StoredRow[];
  reg: StudyRegistry;
  sel: string;
  filter: AfFilter;
  sheet: AfSheet | null;
  busy: string;
  flash: string;
  error: string;
  links: Record<string, string>;
}
let review: Review | null = null;

let reviewLoading = "";
let reviewError = "";

const route = () => location.hash.replace(/^#\/?/, "").split("/").filter(Boolean).map(decodeURIComponent);
const messageOf = (e: unknown) => (e instanceof Error ? e.message.replace(/^.*?\): /, "") : String(e));

/** Browser text widths in points, for the label preview. */
const canvas = typeof document !== "undefined" ? document.createElement("canvas").getContext("2d") : null;
const measure: Measure = (t, size, bold) => {
  if (!canvas) return roughMeasure(t, size, bold);
  canvas.font = `${bold ? "bold " : ""}${size}px Helvetica, Arial, sans-serif`;
  return canvas.measureText(t).width;
};

const blankInfo = (): Record<keyof StudyInfo, string> => ({ firm: "KE", ieaBy: "", jobNumber: "", assessmentDate: "", engineer: "", method: "IEEE 1584-2018" });
const sortRows = (rows: StoredRow[]) => rows.sort((a, b) => (a.match.assetId || `~${a.bus}`).localeCompare(b.match.assetId || `~${b.bus}`, "en", { numeric: true }));

/** Add Site made a new site: read the site list again on the next open (SITE_GROWTH_FIXES_WORDS.md item 1). */
export function dropAfSites(): void {
  sites = null;
  sitesError = "";
}

function draw(): void {
  if (!host) return;
  const h = host;
  const p = route();
  if (!sites) {
    if (!sitesError) void loadSites();
    return h.frame(sitesError ? retryNote(sitesError) : loadingScreen("the sites"));
  }
  if (!sites.length) return h.frame(errorScreen("Your badge covers no site."));
  const site = sites.find((s) => s.siteNumber === p[1]);
  if (!site) {
    location.replace(`#/af-labels/${sites[0]!.siteNumber}`);
    return;
  }
  // Left a study that failed to open: the next open tries again (NAV_CRAWL_WORDS.md A5).
  if (p[2] !== "s" && reviewError) {
    reviewError = "";
    reviewLoading = "";
  }
  if (p[2] === "import") return h.frame(importScreen(site, formOf(site.siteNumber)));
  if (p[2] === "s" && p[3]) return drawReview(site, p[3]);
  const list = studies.get(site.siteNumber);
  if (list === undefined) void loadStudies(site.siteNumber);
  h.frame(studyList(sites, site.siteNumber, typeof list === "string" ? [] : (list ?? null), typeof list === "string" ? list : ""));
}

/** A read that failed: the reason, and Try Again (NAV_CRAWL_WORDS.md A5). */
function retryNote(message: string): string {
  return `<div class="note red">${esc(message)} <button class="chg" type="button" data-af="retry">Try Again</button></div>`;
}

/** Try Again: forget the failed reads on this page so the next draw reads them again. */
function retryReads(): void {
  if (!sites) sitesError = "";
  const site = route()[1] ?? "";
  if (typeof studies.get(site) === "string") studies.delete(site);
  if (reviewError) {
    reviewError = "";
    reviewLoading = "";
  }
  redraw();
}

function drawReview(site: AfSite, studyId: string): void {
  const h = host!;
  const key = `${site.siteNumber}/${studyId}`;
  if (!review || review.key !== key) {
    if (reviewError && reviewLoading === key) return h.frame(`<a class="back" href="#/af-labels/${site.siteNumber}">Arc Flash Labels</a>${retryNote(reviewError)}`);
    if (reviewLoading !== key) void loadReview(site.siteNumber, studyId);
    // A way back while a big study loads (NAV_AUDIT.md item 13).
    return h.frame(`<a class="back" href="#/af-labels/${site.siteNumber}">Arc Flash Labels</a>${loadingScreen("the study")}`);
  }
  const r = review;
  h.frame(
    reviewScreen({ site, study: r.study, rows: r.rows, sel: r.sel, filter: r.filter, busy: r.busy, flash: r.flash, error: r.error, measure, links: r.links, driveLine: "" }) +
      sheetHtml(r.sheet, r.rows.find((x) => x.key === (r.sheet && "key" in r.sheet ? r.sheet.key : "")), !!r.busy) +
      (afPrint && afPrint.key === r.key ? afCardHtml(r, afPrint) : ""),
  );
}

function formOf(site: string) {
  let f = forms.get(site);
  if (!f) {
    f = { info: blankInfo(), files: [], reading: false, busy: "", error: "", raw: [], read: [] };
    forms.set(site, f);
  }
  return f;
}

// ---- Loading ----

async function loadSites(): Promise<void> {
  try {
    sites = await loadAfSites(host!.badge.company, host!.badge.sites);
  } catch (e) {
    sitesError = `Could not read the sites: ${messageOf(e)}`;
  }
  redraw();
}

async function loadStudies(site: string): Promise<void> {
  studies.set(site, []);
  try {
    studies.set(site, await loadAfStudies(host!.badge.company, site));
  } catch (e) {
    studies.set(site, `Could not read the studies: ${messageOf(e)}`);
  }
  redraw();
}

async function loadReview(site: string, studyId: string, keep?: Partial<Review>): Promise<void> {
  const key = `${site}/${studyId}`;
  reviewLoading = key;
  reviewError = "";
  try {
    const c = host!.badge.company;
    const [list, rows, reg] = await Promise.all([loadAfStudies(c, site), loadAfRows(c, site, studyId), loadAfRegistry(c, site)]);
    studies.set(site, list);
    const study = list.find((s) => s.studyId === studyId);
    if (!study) throw new Error("That study is not on file.");
    sortRows(rows);
    const first = rows.find((r) => !r.noLabel && !isReady(r)) ?? rows[0];
    // Open links for every saved label file, so each card is a real link (an iPad blocks a window opened after a wait).
    const links: Record<string, string> = {};
    for (const set of study.labelSets ?? []) for (const f of set.files) links[f.storagePath] = await afStudyFileUrl(f.storagePath).catch(() => "");
    review = { key, study, rows, reg: new StudyRegistry(site, reg as RegistryAsset[]), sel: keep?.sel ?? first?.key ?? "", filter: keep?.filter ?? "All", sheet: null, busy: "", flash: keep?.flash ?? "", error: "", links };
  } catch (e) {
    reviewError = messageOf(e);
  }
  redraw();
}

function redraw(): void {
  if (route()[0] === "af-labels") draw();
}

// ---- Import ----

async function readFiles(site: string, list: FileList): Promise<void> {
  const f = formOf(site);
  f.reading = true;
  f.error = "";
  redraw();
  try {
    const XLSX = await import("xlsx");
    const raw = [...list].slice(0, 10);
    const read: ReadFile[] = [];
    for (const file of raw) {
      if (/\.pdf$/i.test(file.name)) {
        read.push({ name: file.name, sheets: [] });
        continue;
      }
      const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
      read.push({
        name: file.name,
        sheets: wb.SheetNames.map((n) => ({ name: n, rows: XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[n]!, { header: 1, defval: "", raw: true, blankrows: true }) })),
      });
    }
    f.raw = raw;
    f.read = read;
    f.files = sortStudyFiles(read);
  } catch (e) {
    f.error = `A file could not be read: ${messageOf(e)}`;
  }
  f.reading = false;
  redraw();
}

function rowsFor(f: { files: FileSort[]; read: ReadFile[] }, role: FileSort["role"]) {
  const s = f.files.find((x) => x.role === role);
  if (!s) return undefined;
  return f.read.find((r) => r.name === s.name)?.sheets.find((x) => x.name === s.sheet)?.rows;
}

async function runImport(site: string): Promise<void> {
  const f = formOf(site);
  f.error = "";
  try {
    f.busy = "Uploading KE's files…";
    redraw();
    const used = f.raw.filter((file) => f.files.some((x) => x.name === file.name && x.role !== "not used"));
    const files = [];
    for (const file of used) files.push({ uploadName: await uploadAfStudyFile(host!.badge.company, site, file), fileName: file.name });
    f.busy = "Building the label rows…";
    redraw();
    const res = await callServer<{ studyId: string }>("importStudy", {
      siteNumber: site,
      info: f.info,
      detail: rowsFor(f, "detail"),
      summary: rowsFor(f, "summary") ?? null,
      settings: rowsFor(f, "settings") ?? null,
      files,
    });
    forms.delete(site);
    studies.delete(site);
    review = null;
    location.hash = `#/af-labels/${site}/s/${encodeURIComponent(res.studyId)}`;
  } catch (e) {
    f.busy = "";
    f.error = messageOf(e);
    redraw();
  }
}

// ---- Review ----

function sheetFor(kind: "aid" | "iso" | "ir", row: StoredRow): AfSheet {
  const r = review!;
  if (kind === "ir") return { kind, key: row.key, typed: row.interruptingKa === null ? "" : String(row.interruptingKa), error: "" };
  if (kind === "aid") {
    const options = [
      ...(row.match.state === "Suggested" ? [{ id: row.match.assetId, note: row.match.note }] : []),
      ...r.reg.near(row.bus).map((id) => ({ id, note: "In the site's assets" })),
    ].filter((o, i, a) => a.findIndex((x) => x.id === o.id) === i);
    return { kind, key: row.key, pick: row.noLabel ? "__no" : row.match.state === "Matched" ? row.match.assetId : "", options, typed: "", error: "" };
  }
  const options: { id: string; note: string }[] = [];
  if (row.isolateAt.assetId) options.push({ id: row.isolateAt.assetId, note: row.isolateAt.note || "From the winning reading" });
  for (const x of row.readings) {
    if (x.device === row.failedDevice) continue;
    const m = r.reg.matchDevice(x.device);
    if (m.assetId && !options.some((o) => o.id === m.assetId)) options.push({ id: m.assetId, note: `SKM: ${x.device}${x.incoming ? " (Incoming reading)" : ""}` });
  }
  if (!options.length || /^(MaxTripTime|IndMtr)/.test(row.winner.device))
    for (const a of r.reg.byId.values()) if (/utility/i.test(a.equipmentType ?? "") && !options.some((o) => o.id === a.assetId)) options.push({ id: a.assetId, note: "Utility service. SKM's worst case here is its 2-second cap." });
  return { kind, key: row.key, pick: row.isolateAt.state === "Matched" ? row.isolateAt.assetId : "", options, typed: "", error: "" };
}

async function sendChange(row: StoredRow, change: RowChange): Promise<boolean> {
  const r = review!;
  r.busy = "Saving";
  r.error = "";
  redraw();
  try {
    const res = await callServer<{ row: StoredRow; counts: AfStudySummary["counts"] }>("setLabelRow", { siteNumber: r.study.siteNumber, studyId: r.study.studyId, rowKey: row.key, change });
    r.rows = sortRows(r.rows.map((x) => (x.key === row.key ? res.row : x)));
    r.study = { ...r.study, counts: res.counts };
    r.busy = "";
    return true;
  } catch (e) {
    r.busy = "";
    if (r.sheet) r.sheet = { ...r.sheet, error: messageOf(e) };
    else r.error = messageOf(e);
    redraw();
    return false;
  }
}

async function saveSheet(): Promise<void> {
  const r = review!;
  const s = r.sheet;
  if (!s) return;
  if (s.kind === "publish") return publish();
  const row = r.rows.find((x) => x.key === s.key);
  if (!row) return;
  let change: RowChange | null = null;
  if (s.kind === "aid") {
    const v = s.typed.trim() || s.pick;
    if (v === "__no") change = { field: "noLabel", value: true };
    else if (v) change = { field: "assetId", value: v };
  } else if (s.kind === "iso") {
    const v = s.typed.trim() || s.pick;
    if (v) change = { field: "isolateAt", value: v };
  } else {
    const n = parseFloat(s.typed);
    if (!(n > 0)) {
      r.sheet = { ...s, error: "Enter the rating in kA." };
      return redraw();
    }
    change = { field: "interruptingKa", value: n };
  }
  if (!change) {
    r.sheet = { ...s, error: s.kind === "aid" ? "Pick an Asset ID, type one, or choose No Label." : "Pick or type the Asset ID to isolate at." };
    return redraw();
  }
  if (await sendChange(row, change)) {
    r.sheet = null;
    redraw();
  }
}

async function publish(): Promise<void> {
  const r = review!;
  r.busy = "Publishing";
  redraw();
  try {
    const res = await callServer<{ published: number; assets: number; check: { allGood: boolean; misses: string[] } }>("publishStudy", { siteNumber: r.study.siteNumber, studyId: r.study.studyId, confirmed: true });
    const flash = `Published ${res.published} ${res.published === 1 ? "label" : "labels"} to ${res.assets} assets. ${res.check.allGood ? "Publish Check: all good." : `Publish Check found ${res.check.misses.length} to fix.`}`;
    await loadReview(r.study.siteNumber, r.study.studyId, { sel: r.sel, filter: r.filter, flash });
  } catch (e) {
    r.busy = "";
    if (r.sheet?.kind === "publish") r.sheet = { ...r.sheet, error: messageOf(e) };
    redraw();
  }
}

// ---- Print Arc Flash Labels: the one print card, rev 5 (COHESION_CHANGE_LOG.md list 2, MOCKUP_ONE_PRINT_REV5.html) ----
// Drive keeps two PDFs: 10_AF_Labels_WARNING.pdf and 10_AF_Labels_DANGER.pdf, one per roll, room by room.
// A new study changes every label (the fingerprint holds the assessment date), so Update PDF rebuilds both.

interface AfPrint {
  key: string;
  /** What printed for each Asset ID at the site (rule 37). Null while it loads. */
  prints: Record<string, AfPrintRecord> | null;
  loadError: string;
  pc: PcState;
}
let afPrint: AfPrint | null = null;

const ROLL: Record<AfStock, { sub: string; color: string; file: (typeof LABEL_FILES)[number] }> = {
  WARNING: { sub: "load the WARNING roll", color: "#f26b1d", file: "AF_Labels_WARNING_Print.pdf" },
  DANGER: { sub: "then the DANGER roll", color: "#c8102e", file: "AF_Labels_DANGER_Print.pdf" },
};

function infoOf(r: Review): StudyInfo {
  const s = r.study;
  return { firm: s.firm, ieaBy: s.ieaBy, jobNumber: s.jobNumber, assessmentDate: s.assessmentDate, engineer: s.engineer, method: s.method };
}

/** Every row marked Print and published, as its label would print now. */
function afCandidates(r: Review): AfCandidate[] {
  return labelRecordsOf(r.rows, r.study.siteNumber, infoOf(r), r.study.studyId).map((x) => candidateOf(x.rec));
}

/** The room in the Asset ID: 10-ER1-MCC-01 is ER1; outside gear has none. */
const roomOfAsset = (id: string) => {
  const part = id.split("-")[1] ?? "";
  return /^(ER|EA|EL)\d/.test(part) ? part : "";
};

function afArt(c: AfCandidate): string {
  const v = c.values;
  const head = c.stock === "DANGER" ? "d" : "w";
  const lines = v ? `<b>${esc(v.ie)} cal/cm² · AFB ${esc(v.afb)}</b><span>${esc(v.voltage)}</span><span class="id">${esc(c.assetId)}</span>` : `<span class="id">${esc(c.assetId)}</span><span>Can't print</span>`;
  return `<span class="pc-art af"><span class="pc-afh ${head}">${c.stock}</span><span class="pc-afb">${lines}</span></span>`;
}

/** The two rolls, every label marked Print and published, room by room. Exported for the tests. */
export function afRolls(r: Review, prints: Record<string, AfPrintRecord>): PcRoll[] {
  const all = afCandidates(r);
  return (["WARNING", "DANGER"] as const)
    .map((stock): PcRoll => ({
      key: stock,
      title: stock,
      sub: ROLL[stock].sub,
      driveName: masterName("af", r.study.siteNumber, stock),
      folder: "06 Arc Flash Labels",
      withRooms: true,
      rollColor: ROLL[stock].color,
      roomLine: (_room, n) => `${n} label${n === 1 ? "" : "s"} · ${stock}`,
      items: all
        .filter((c) => c.stock === stock)
        .map((c) => {
          const st = afPrintState(c, prints[c.assetId]);
          return {
            key: c.assetId,
            room: roomOfAsset(c.assetId),
            state: st.state,
            why: !c.values ? (c.skip[0] ?? "Can't print") : st.state === "changed" ? st.line : st.state === "new" ? "Not printed yet" : "",
            reprintWhy: st.state === "current" ? `Last printed ${shortDate(st.at)}` : undefined,
            art: afArt(c),
            fix: !c.values,
            skip: !c.values,
          };
        }),
    }))
    .filter((x) => x.items.length > 0);
}

function afCardHtml(r: Review, p: AfPrint): string {
  if (p.prints === null) {
    const note = p.loadError ? `Could not read what printed: ${esc(p.loadError)}` : "Reading what printed at this site…";
    return printCard({ title: "Print Arc Flash Labels", roll: "arcFlashRoll", rolls: [], state: p.pc, note });
  }
  const rolls = afRolls(r, p.prints);
  seedPicks(p.pc, rolls);
  const skipped = rolls.flatMap((x) => x.items).filter((i) => i.skip).length;
  const top = skipped ? `<div class="note">${skipped} label${skipped === 1 ? "" : "s"} can't print yet. Tap Fix to open the row. They go on the Exceptions list.</div>` : "";
  return printCard({ title: "Print Arc Flash Labels", roll: "arcFlashRoll", rolls, state: p.pc, top });
}

async function openAfPrint(): Promise<void> {
  const r = review!;
  afPrint = { key: r.key, prints: null, loadError: "", pc: newPcState() };
  const p = afPrint;
  redraw();
  void loadMasterState(p.pc, host!.badge.company, r.study.siteNumber, redraw);
  try {
    p.prints = await loadAfLabelPrints(host!.badge.company, r.study.siteNumber);
  } catch (e) {
    p.loadError = messageOf(e);
  }
  redraw();
}

/**
 * Update PDF for one roll. Every label on the roll, room by room with a room page first, at the roll's start
 * offset, drawn by the same code as always. Kept on the study as a set, the way Create Labels did: the print
 * PDF, the Preview PDF and the Exceptions list. The server notes the set and keeps what printed for each
 * Asset ID (rule 37). Then the PDF goes to 06 Arc Flash Labels, replacing the one there.
 */
async function updateAfRolls(stocks: readonly AfStock[]): Promise<void> {
  const r = review!;
  const p = afPrint!;
  if (p.pc.busy || p.prints === null || !stocks.length) return;
  const s = r.study;
  p.pc.busy = stocks.length > 1 ? "update:all" : `update:${stocks[0]}`;
  p.pc.error = "";
  redraw();
  try {
    const { buildLabelSet } = await import("./af-label-pdf");
    const all = afCandidates(r);
    const rolls = afRolls(r, p.prints);
    const flashes: string[] = [];
    for (const stock of stocks) {
      const roll = rolls.find((x) => x.key === stock);
      if (!roll) continue;
      const going = all.filter((c) => c.stock === stock);
      const byId = new Map(going.map((c) => [c.assetId, c]));
      const parts = await Promise.all(
        sectionsOf(roll).map(async (sec) => {
          const set = await buildLabelSet(sec.items.map((i, k) => ({ row: k + 2, rec: byId.get(i.key)!.record })));
          return { room: sec.room, line: roll.roomLine!(sec.room, sec.items.length), bytes: (stock === "WARNING" ? set.warning : set.danger) ?? new Uint8Array() };
        }),
      );
      const master = await assembleMaster(`Alltech Arc Flash Labels (${stock} stock), site ${s.siteNumber}`, parts.filter((x) => x.bytes.length));
      // The set: this roll's labels, the Preview PDF and Exceptions list as before, the master as its print PDF.
      const set = await buildLabelSet(going.map((c, i) => ({ row: i + 2, rec: c.record })));
      const setId = labelSetIdOf(new Date().toISOString());
      const body: Record<(typeof LABEL_FILES)[number], Uint8Array | string | null> = {
        "AF_Labels_WARNING_Print.pdf": stock === "WARNING" ? master : null,
        "AF_Labels_DANGER_Print.pdf": stock === "DANGER" ? master : null,
        "AF_Labels_Preview.pdf": set.preview,
        "AF_Label_Exceptions.csv": set.exceptionsCsv,
      };
      const files: string[] = [];
      for (const name of LABEL_FILES) {
        const data = body[name];
        if (data === null) continue;
        await uploadAfLabelFile(host!.badge.company, s.siteNumber, s.studyId, setId, name, data);
        files.push(name);
      }
      const printed = going.filter((c) => c.values);
      const entries = printedEntries(printed);
      await callServer("recordLabelSet", { siteNumber: s.siteNumber, studyId: s.studyId, setId, files, counts: set.counts, printed: entries });
      const at = new Date().toISOString();
      for (const e of entries) p.prints![e.assetId] = { ...e, at, studyId: s.studyId, setId };
      p.pc.masterFiles.set(stock, new File([master as Uint8Array<ArrayBuffer>], roll.driveName, { type: "application/pdf" }));
      await sendMaster(p.pc, { company: host!.badge.company, site: s.siteNumber, kind: "af", driveName: roll.driveName, bytes: master, badgeId: host!.badge.badgeId, byName: host!.badge.holderName }, redraw);
      const c = set.counts;
      flashes.push(`${stock} PDF updated: ${stock === "WARNING" ? c.warning : c.danger} labels.${c.skipped ? ` ${c.skipped} skipped, see Exceptions.` : ""}`);
    }
    p.pc.busy = "";
    await loadReview(s.siteNumber, s.studyId, { sel: r.sel, filter: r.filter, flash: flashes.join(" ") });
  } catch (e) {
    p.pc.busy = "";
    p.pc.error = `Update PDFs stopped: ${messageOf(e)}`;
    redraw();
  }
}

/** Make PDF: just the picked labels on one roll, at this iPad's offset. For the printer. Never to Drive. */
async function makeAfSmall(stock: AfStock): Promise<void> {
  const r = review!;
  const p = afPrint!;
  if (p.pc.busy || p.prints === null) return;
  const roll = afRolls(r, p.prints).find((x) => x.key === stock);
  if (!roll) return;
  const ids = new Set(pickedOf(roll, p.pc).map((i) => i.key));
  const going = afCandidates(r).filter((c) => c.stock === stock && ids.has(c.assetId) && c.values);
  if (!going.length) return;
  p.pc.busy = `make:${stock}`;
  p.pc.error = "";
  redraw();
  try {
    const { buildLabelSet } = await import("./af-label-pdf");
    const set = await buildLabelSet(going.map((c, i) => ({ row: i + 2, rec: c.record })), undefined, undefined, readOffset("arcFlashRoll"));
    const bytes = stock === "WARNING" ? set.warning : set.danger;
    if (bytes) p.pc.small.set(stock, { file: new File([bytes as Uint8Array<ArrayBuffer>], `${r.study.siteNumber}_AF_Labels_Print_${stock}.pdf`, { type: "application/pdf" }), count: going.length });
  } catch (e) {
    p.pc.error = `Make PDF stopped: ${messageOf(e)}`;
  }
  p.pc.busy = "";
  redraw();
}

/** A tap on the card. */
async function afCardClick(el: HTMLElement): Promise<void> {
  const p = afPrint!;
  const r = review!;
  const rolls = p.prints ? afRolls(r, p.prints) : [];
  const a = cardClick(el, p.pc, rolls);
  if (!a) return;
  const stock: AfStock = "block" in a && a.block === "DANGER" ? "DANGER" : "WARNING";
  switch (a.act) {
    case "close":
      if (p.pc.busy) return;
      stopMasterWatches();
      afPrint = null;
      break;
    case "setup":
      stopMasterWatches();
      afPrint = null;
      location.hash = "#/settings/printer";
      return;
    case "fix": {
      const row = r.rows.find((x) => x.match.assetId === a.key);
      if (row) r.sel = row.key;
      stopMasterWatches();
      afPrint = null;
      break;
    }
    case "update":
      await updateAfRolls([stock]);
      return;
    case "updateall":
      await updateAfRolls(rolls.filter((x) => rollStatus(x, p.pc).behind).map((x) => x.key as AfStock));
      return;
    case "make":
      await makeAfSmall(stock);
      return;
    case "share": {
      const f = p.pc.small.get(stock)?.file;
      if (f) await shareFile(f);
      return;
    }
    case "download": {
      const f = p.pc.small.get(stock)?.file;
      if (f) downloadFile(f);
      return;
    }
    case "mdownload": {
      const f = p.pc.masterFiles.get(stock);
      if (f) downloadFile(f);
      return;
    }
    case "retry":
      void retryMaster(p.pc, r.study.siteNumber, masterName("af", r.study.siteNumber, stock), redraw);
      return;
    default:
      break;
  }
  redraw();
}

async function runCheck(): Promise<void> {
  const r = review!;
  r.busy = "Checking";
  redraw();
  try {
    await callServer("checkPublish", { siteNumber: r.study.siteNumber, studyId: r.study.studyId });
    await loadReview(r.study.siteNumber, r.study.studyId, { sel: r.sel, filter: r.filter, flash: "" });
  } catch (e) {
    r.busy = "";
    r.error = messageOf(e);
    redraw();
  }
}

// ---- Events ----

function wire(root: HTMLElement): void {
  if (wired) return;
  wired = true;
  root.addEventListener("click", (e) => {
    if (route()[0] !== "af-labels") return;
    const pc = (e.target as HTMLElement).closest<HTMLElement>("[data-pc]");
    if (pc && afPrint && review && afPrint.key === review.key) {
      if (!(pc as HTMLButtonElement).disabled) void afCardClick(pc);
      return;
    }
    if ((e.target as HTMLElement).matches("[data-pc-sheet]") && afPrint && !afPrint.pc.busy) {
      stopMasterWatches();
      afPrint = null;
      return redraw();
    }
    const t = (e.target as HTMLElement).closest<HTMLElement>("[data-af],[data-af-row],[data-af-print],[data-af-filter],[data-af-edit],[data-af-pick]");
    if (!t) return;
    const site = route()[1] ?? "";
    const r = review;
    if (t.dataset.af === "import") return void runImport(site);
    if (t.dataset.af === "retry") return retryReads();
    if (!r) return;
    if (t.dataset.af === "shade" && e.target === t) {
      r.sheet = null;
      return redraw();
    }
    if (t.dataset.af === "close") {
      r.sheet = null;
      return redraw();
    }
    if (t.dataset.af === "save") return void saveSheet();
    if (t.dataset.af === "check") return void runCheck();
    if (t.dataset.af === "create") return void openAfPrint();
    if (t.dataset.af === "publish") {
      const labels = r.rows.filter((x) => isReady(x) && x.print && !x.published);
      r.sheet = { kind: "publish", labels: labels.length, assets: labels.reduce((n, x) => n + 1 + x.buckets.length, 0), error: "" };
      return redraw();
    }
    if (t.dataset.afFilter) {
      r.filter = t.dataset.afFilter as AfFilter;
      return redraw();
    }
    if (t.dataset.afPrint) {
      e.stopPropagation();
      const row = r.rows.find((x) => x.key === t.dataset.afPrint);
      if (row) void sendChange(row, { field: "print", value: !row.print }).then(() => redraw());
      return;
    }
    if (t.dataset.afEdit) {
      const row = r.rows.find((x) => x.key === r.sel);
      if (row) r.sheet = sheetFor(t.dataset.afEdit as "aid" | "iso" | "ir", row);
      return redraw();
    }
    if (t.dataset.afPick !== undefined && r.sheet && "pick" in r.sheet) {
      r.sheet = { ...r.sheet, pick: t.dataset.afPick, typed: "" };
      return redraw();
    }
    if (t.dataset.afRow) {
      r.sel = t.dataset.afRow;
      r.flash = "";
      return redraw();
    }
  });
  root.addEventListener("change", (e) => {
    if (route()[0] !== "af-labels") return;
    const t = e.target as HTMLInputElement | HTMLSelectElement;
    if (t.dataset.af === "site") location.hash = `#/af-labels/${t.value}`;
    if (t.dataset.af === "files" && t instanceof HTMLInputElement && t.files?.length) void readFiles(route()[1] ?? "", t.files);
  });
  root.addEventListener("input", (e) => {
    if (route()[0] !== "af-labels") return;
    const t = e.target as HTMLInputElement;
    if (afPrint && cardFind(t, afPrint.pc, redraw, host!.root)) return;
    if (t.dataset.info) formOf(route()[1] ?? "").info[t.dataset.info as keyof StudyInfo] = t.value;
    if (t.dataset.afTyped && review?.sheet && "typed" in review.sheet) review.sheet = { ...review.sheet, typed: t.value, error: "" } as AfSheet;
  });
  root.addEventListener("keydown", (e) => {
    if (route()[0] !== "af-labels") return;
    const t = e.target as HTMLElement;
    if (e.key === "Escape" && review?.sheet) {
      review.sheet = null;
      return redraw();
    }
    if ((e.key === "Enter" || e.key === " ") && t.dataset.afRow && review) {
      e.preventDefault();
      review.sel = t.dataset.afRow;
      redraw();
    }
  });
}

export function renderAfLabels(h: AfHost): void {
  // Keep the table's own scroll when a row is tapped or a change comes back.
  host = {
    ...h,
    frame: (inner) => {
      const box = h.root.querySelector<HTMLElement>(".af-tablebox");
      const top = box?.scrollTop ?? 0;
      h.frame(inner);
      const again = h.root.querySelector<HTMLElement>(".af-tablebox");
      if (again && top) again.scrollTop = top;
    },
  };
  wire(h.root);
  draw();
}
